import { formatDate, yen } from "../format";
import type { CalcResult, StoreRecord } from "../types";

interface Props {
  result: CalcResult;
  rank: number;
  store?: StoreRecord;
}

export default function ResultCard({ result, rank, store }: Props) {
  const checkedAt = formatDate(store?.source.checkedAt ?? store?.source.fetchedAt ?? null);
  const rules = (store?.rules ?? []).filter((rule) => rule.value !== null);
  const unconfirmed = (store?.rules ?? []).length - rules.length;

  return (
    <article
      className={`rounded-2xl border bg-white p-4 shadow-sm ${
        rank === 1 ? "border-brand-300 ring-1 ring-brand-100" : "border-slate-200"
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
            rank === 1 ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600"
          }`}
          aria-hidden="true"
        >
          {rank}
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-bold leading-tight">
            {result.storeName}
            <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 align-middle text-[0.65rem] font-medium text-slate-500">
              {result.area}
            </span>
          </h2>
          <p className="mt-0.5 text-sm text-slate-600">
            {result.planName}（{result.usageType}）
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <strong className="text-2xl font-extrabold tracking-tight text-brand-600">
          {yen(result.totalPerPerson)}
        </strong>
        <span className="text-xs text-slate-500">/ 1人あたり</span>
        <span className="text-xs text-slate-500">
          グループ {yen(result.totalGroup)}（{result.numPeople}名）
        </span>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="chip">{result.drinkCondition}</span>
        <span className="chip">{result.memberType}</span>
        <span className="chip">対象: {result.targetAudience}</span>
        {checkedAt ? (
          <span className="chip bg-emerald-50 text-emerald-700">確認日 {checkedAt}</span>
        ) : (
          <span className="chip bg-rose-50 text-rose-700">確認日 未登録</span>
        )}
      </div>

      {result.warnings.length > 0 && (
        <ul className="mt-3 space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
          {result.warnings.map((warning) => (
            <li key={warning}>⚠ {warning}</li>
          ))}
        </ul>
      )}

      {rules.length > 0 && (
        <dl className="mt-3 grid grid-cols-[6.5em_1fr] gap-x-2 gap-y-0.5 text-[0.72rem] text-slate-500">
          {rules.slice(0, 6).map((rule) => (
            <div key={rule.key} className="contents">
              <dt className="text-slate-400">{rule.label}</dt>
              <dd>{String(rule.value)}</dd>
            </div>
          ))}
        </dl>
      )}

      {unconfirmed > 0 && (
        <p className="mt-1 text-[0.68rem] text-rose-600">未確認の条件: {unconfirmed}件</p>
      )}

      {result.breakdownNote && (
        <p className="mt-2 break-words text-[0.72rem] leading-relaxed text-slate-500">
          {result.breakdownNote}
        </p>
      )}
    </article>
  );
}
